import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { connectorGetAPI } from 'api/connector'
import loginData from 'utilities/loginData'
import { ContractStatusType } from 'api/common/constants'

interface IdentificationStatusState {
  acceptedStatus: boolean | null
  isLoading: boolean
  contractStatus: ContractStatusType | null
  apiCallStatus: string
}

/**
 * 本人確認状態を管理するカスタムフック
 * 
 * 【フック概要】
 * ユーザーの本人確認（KYC）完了状態を管理するカスタムフック
 * コネクターAPIから最新の契約状態と本人確認ステータスを取得し、画面制御に必要な情報を提供
 * 
 * 【取得する情報】
 * - acceptedStatus: 本人確認の完了状態（true/false/null）
 * - contractStatus: 契約ステータス（文字列）
 * - isLoading: データ取得中フラグ
 * - apiCallStatus: API呼び出しの状態（デバッグ用）
 * 
 * 【主な機能】
 * - ログイン状態のチェックと自動リダイレクト
 * - コネクター情報の自動取得と更新
 * - 本人確認開始可能かの判定
 * - 本人確認完了済みかの判定
 * 
 * 【使用場面】
 * - RegisterIdentificationForIndividualコンポーネント
 * - 本人確認状態に応じた画面制御が必要な箇所
 * 
 * 【リファクタリング内容（2025-07-22）】
 * - RegisterIdentificationForIndividualから状態管理ロジックを抽出
 * - Promise化によるAPI呼び出しの改善
 * - エラーハンドリングの強化
 * - TypeScript型定義（IdentificationStatusState）の追加
 * - 便利なフラグ（canStartVerification、isVerified）の提供
 * 
 * @returns {Object} 本人確認状態管理用のオブジェクト
 * - acceptedStatus: 本人確認完了状態
 * - isLoading: データロード中フラグ
 * - contractStatus: 現在の契約ステータス（数値）
 * - apiCallStatus: API呼び出し状態（デバッグ用）
 * - refreshUserData: 手動でデータを再取得
 * - checkLoginStatus: ログイン状態をチェック
 * - canStartVerification: 本人確認を開始可能か
 * - isVerified: 本人確認済みか
 */
export const useIdentificationStatus = () => {
  const navigate = useNavigate()
  
  const [state, setState] = useState<IdentificationStatusState>({
    acceptedStatus: null,
    isLoading: true,
    contractStatus: null,
    apiCallStatus: '初期状態'
  })

  /**
   * コネクター情報を更新して最新の状態を取得
   */
  const refreshUserData = useCallback(async () => {
    setState(prev => ({ ...prev, apiCallStatus: 'コネクター情報取得開始' }))
    
    try {
      await new Promise<void>((resolve) => {
        connectorGetAPI((status, response) => {
          setState(prev => ({ ...prev, apiCallStatus: `コネクター情報取得: status=${status}` }))
          
          resolve()
        })
      })
      
      // 更新後の状態を取得
      const currentContractStatus = loginData.getContractStatus()
      const identStatus = loginData.getIdentificationRegistrationStatus()
      
      setState({
        acceptedStatus: identStatus,
        isLoading: false,
        contractStatus: currentContractStatus ?? null,
        apiCallStatus: `現在の状態: contractStatus=${currentContractStatus}, identStatus=${identStatus}`
      })
      
    } catch (error) {
      // エラーの場合は現在の値を使用
      const status = loginData.getIdentificationRegistrationStatus()
      setState(prev => ({
        ...prev,
        acceptedStatus: status,
        isLoading: false,
        apiCallStatus: `エラー: ${error}`
      }))
    }
  }, [])

  /**
   * ログイン状態をチェック（副作用なし）
   */
  const isLoggedIn = useCallback(() => {
    return loginData.isLoggedIn()
  }, [])

  /**
   * ログイン状態をチェックしてリダイレクト（副作用あり）
   */
  const checkLoginStatus = useCallback(() => {
    if (!loginData.isLoggedIn()) {
      navigate('/')
      return false
    }
    return true
  }, [navigate])

  // 初期化処理（マウント時のみ実行）
  useEffect(() => {
    const loggedIn = isLoggedIn()
    if (!loggedIn) {
      navigate('/')
      return
    }
    
    // ログインしている場合のみデータを取得
    refreshUserData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // 空の依存配列でマウント時のみ実行

  return {
    ...state,
    refreshUserData,
    checkLoginStatus,
    // 便利なフラグ
    canStartVerification: state.acceptedStatus === null && !state.isLoading,
    isVerified: state.acceptedStatus === true
  }
}